/** @format */

import { useEffect, useRef } from 'react';
import { useLocation, type Location } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import useGa4Analytics from 'src/hooks/useGa4Analytics';
import { isKeycloakAuthRedirect } from 'src/analytics';

function shouldSkipPageView({
  isInitialised,
  isAuthenticated,
  location
}: {
  isInitialised: boolean;
  isAuthenticated: boolean;
  location: Location;
}): boolean {
  if (!isInitialised) return true;
  if (!isAuthenticated) return true; // optional: only track when authenticated
  if (isKeycloakAuthRedirect()) return true; // #code= / ?code=
  if ((location.state as { openAsDrawer?: boolean } | null)?.openAsDrawer) return true; // open drawer, do not change "main page"
  return false;
}

export default function useGa4PageView(): void {
  const { pathname } = useLocation();
  const location = useLocation(); // or destructure state
  const { isInitialised, isAuthenticated } = useAuth();
  const { trackPageView } = useGa4Analytics();
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    if (shouldSkipPageView({ isInitialised, isAuthenticated, location })) {
      return;
    }

    if (lastTrackedPath.current === pathname) {
      return;
    }
    lastTrackedPath.current = pathname;

    trackPageView();
  }, [pathname, isInitialised, isAuthenticated, (location.state as { openAsDrawer?: boolean } | null)?.openAsDrawer]);
}
